import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { CustomInteriorOptionsView, ManualInteriorChange } from "@slfinrtl/ui-bff-routes"
import { getCustomInteriorOptionsRoute, setManualInteriorRoute } from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import type { SubmitHandler, UseFormReturn } from "react-hook-form"
import { useForm } from "react-hook-form"
import { defineMessages, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { propertiesOf } from "src/common/property"
import { getMarketLanguageTranslations } from "src/common/translations"
import { useBff, useBffDynamic } from "src/hooks"
import type { CustomInteriorContainerProps } from "./CustomInteriorContainer"
import type { CustomInteriorColorProps } from "./components/CustomInteriorColor"

export const messages = defineMessages({
	interiorCockpitColorHexCode: {
		defaultMessage: "Shade of Color - Dashboard",
		id: "colors.interior.customInteriorModal.interiorCockpitColorHexCode",
	},
	interiorSeatColorHexCode: {
		defaultMessage: "Shade of Color - Seats",
		id: "colors.interior.customInteriorModal.interiorSeatColorHexCode",
	},
	material: {
		defaultMessage: "Material",
		id: "colors.interior.customInteriorModal.material",
	},
	name: {
		defaultMessage: "Interior Name",
		id: "colors.interior.customInteriorModal.name",
	},
	selectCatalogInterior: {
		defaultMessage: "Select Catalog Interior",
		id: "colors.interior.customInteriorModal.selectCatalogInterior",
	},
	setDifferentCockpitColor: {
		defaultMessage: "Set different dashboard color",
		id: "colors.interior.customInteriorModal.setDifferentCockpitColor",
	},
	tag: {
		defaultMessage: "Color Group",
		id: "colors.interior.customInteriorModal.tag",
	},
})

export interface CustomInteriorForm {
	cockpitColorHexCode: string
	materialKey: string
	name: Record<string, string>
	seatColorHexCode: string
	tagKey: string
}

interface UseCustomInteriorReturnType {
	form: UseFormReturn<CustomInteriorForm>
	onSubmit: SubmitHandler<CustomInteriorForm>
	props: CustomInteriorColorProps | undefined
}

export const useCustomInterior = ({
	currentInterior,
	inventoryId,
	onInteriorCreated,
	showCatalogInteriorContainer,
}: CustomInteriorContainerProps): UseCustomInteriorReturnType => {
	const { availableLocales } = useDealerContext()

	const [allOptions, setAllOptions] = useState<CustomInteriorOptionsView>()

	const [hasDifferentCockpitColor, setHasDifferentCockpitColor] = useState(
		currentInterior?.color?.seatColorHexCode !== currentInterior?.color?.cockpitColorHexCode,
	)

	const form = useForm<CustomInteriorForm>({
		defaultValues: {
			cockpitColorHexCode: currentInterior?.color?.cockpitColorHexCode ?? "",
			materialKey: currentInterior?.materialKey ?? "",
			name: getMarketLanguageTranslations(availableLocales, currentInterior?.name ?? {}),
			seatColorHexCode: currentInterior?.color?.seatColorHexCode ?? "",
			tagKey: currentInterior?.color?.tagKey ?? "",
		},
	})

	const getCustomInteriorOptions = useBff(
		getCustomInteriorOptionsRoute,
		{
			body: undefined,
			pathParams: {},
			queryParams: {},
		},
		"inline",
	)

	useEffect(() => {
		getCustomInteriorOptions().then(setAllOptions)
	}, [getCustomInteriorOptions])

	const setManualInterior = useBffDynamic(setManualInteriorRoute, "inline")

	const [hasSubmitFailed, setHasSubmitFailed] = useState(false)
	const onSubmit: SubmitHandler<CustomInteriorForm> = async (formState) => {
		const manualInteriorChange: ManualInteriorChange = {
			cockpitColorHexCode: formState.cockpitColorHexCode ? formState.cockpitColorHexCode : undefined,
			materialKey: formState.materialKey,
			name: getMarketLanguageTranslations(availableLocales, formState.name),
			seatColorHexCode: formState.seatColorHexCode,
			tagKey: formState.tagKey,
		}

		await setManualInterior({
			body: manualInteriorChange,
			pathParams: {},
			queryParams: {
				"inventory-id": inventoryId,
				"market-languages": availableLocales,
			},
		})
			.then(onInteriorCreated)
			.catch(() => setHasSubmitFailed(true))
	}

	const { formatDisplayName, formatMessage } = useIntl()

	return {
		form,
		onSubmit,
		props: allOptions && {
			cockpitColorHexCode: {
				label: formatMessage(messages.interiorCockpitColorHexCode),
				name: customInteriorFormProperties.cockpitColorHexCode._path,
				required: hasDifferentCockpitColor,
			},
			differentCockpitColor: {
				label: formatMessage(messages.setDifferentCockpitColor),
				onChange: (value: boolean) => {
					form.setValue("cockpitColorHexCode", "")
					setHasDifferentCockpitColor(value)
				},
				value: hasDifferentCockpitColor,
			},
			hasSubmitFailed,
			isSubmitting: form.formState.isSubmitting,
			material: {
				label: formatMessage(messages.material),
				name: customInteriorFormProperties.materialKey._path,
				options: allOptions.materials.map((material) => ({
					label: material.name,
					value: material.key,
				})),
				required: true,
			},
			name: availableLocales.map((languageTag) => ({
				label: `${formatMessage(messages.name)} - ${formatDisplayName(languageTag, {
					type: "language",
				})}`,
				name: `${customInteriorFormProperties.name._path}.${languageTag}`,
				required: true,
				type: "text",
				validate: (value: string | number) =>
					value.toString() !== value.toString().toUpperCase() ||
					formatMessage(commonMessages.onlyCapitalLetters),
			})),
			seatColorHexCode: {
				label: formatMessage(messages.interiorSeatColorHexCode),
				name: customInteriorFormProperties.seatColorHexCode._path,
				required: true,
			},
			setHasSubmitFailed,
			showCatalogInteriorContainer,
			tag: {
				label: formatMessage(messages.tag),
				name: customInteriorFormProperties.tagKey._path,
				options: allOptions.tags.map((tag) => ({
					hexCode: tag.hexCode,
					label: tag.name,
					value: tag.key,
				})),
				required: true,
			},
		},
	}
}

const customInteriorFormProperties = propertiesOf<CustomInteriorForm>()
